import React, { useEffect, useState } from "react";
import { Modal, Table, Button, Flex, Input, notification } from "antd";
import { SearchZoomIn1 } from "iconsax-react";
import { mixPostV1 } from "../../../services/mixin";
import type { Dayjs } from "dayjs";
import type { TableColumnsType } from "antd";

export interface ShiftDataType {
  key: React.Key;
  report_id: number;
  date_report: string;
  staff_name: string;
  addbank: number;
  profit: number;
  money_of_system: number;
  money_in_proof: number;
  zelle: number;
  baccarat: number;
  loss: number;
  final: number;
  bonus_total: number;
  labor: number;
  match_total: number;
  extra: number;
  payout_total: number;
}

const numberFormat = (number: any) => {
  if (typeof number === "number") {
    return new Intl.NumberFormat().format(number);
  }
  return number;
};

interface Props {
  isShow: boolean;
  fromDate: Dayjs;
  toDate: Dayjs;
  initialSelectedKeys: React.Key[];
  onClose: () => void;
  onSubmit: (selectedKeys: React.Key[], selectedRows: ShiftDataType[]) => void;
}

const ShiftFilterModal: React.FC<Props> = ({
  isShow,
  fromDate,
  toDate,
  initialSelectedKeys,
  onClose,
  onSubmit,
}) => {
  const [loading, setLoading] = useState(false);
  const [allShifts, setAllShifts] = useState<ShiftDataType[]>([]);
  const [searchText, setSearchText] = useState("");
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<ShiftDataType[]>([]);
  const [api, contextHolder] = notification.useNotification();

  const openNotification = (type: "success" | "error", message: string) => {
    api[type]({
      message,
      placement: "bottomRight",
      className: "single-message",
    });
  };

  useEffect(() => {
    if (isShow) {
      setSelectedRowKeys(initialSelectedKeys);
      fetchShifts();
    }
  }, [isShow, fromDate, toDate]);

  const fetchShifts = async () => {
    setLoading(true);
    try {
      const res = await mixPostV1("/api/webAdmin/greportTotalIncome.php", {
        limit: 1000,
        page_number: 1,
        fromDate: fromDate.format("YYYY-MM-DD 00:00"),
        toDate: toDate.format("YYYY-MM-DD 23:59"),
      });
      if (res.status === 1) {
        const dataList = (res.data ?? []).map((item: any, index: number) => ({
          ...item,
          key: item.report_id,
          date_report: item.date_report,
          staff_name: item.staff_name,
          addbank: Number(item.addbank || 0),
          profit: Number(item.profit || 0),
          money_of_system: Number(item.money_of_system || 0),
          money_in_proof: Number(item.money_in_proof || 0),
          zelle: Number(item.zelle || 0),
          baccarat: Number(item.baccarat || 0),
          loss: Number(item.loss || 0),
          final: Number(item.final || 0),
          bonus_total: Number(item.bonus_total || 0),
          labor: Number(item.labor || 0),
          match_total: Number(item.match_total || 0),
          extra: Number(item.extra || 0),
          payout_total: Number(item.payout_total || 0),
        }));
        setAllShifts(dataList);

        // Keep selected rows data aligned with the fetched list
        if (initialSelectedKeys.length > 0) {
          const matchedRows = dataList.filter((item: ShiftDataType) =>
            initialSelectedKeys.includes(item.report_id),
          );
          setSelectedRows(matchedRows);
        } else {
          setSelectedRows([]);
        }
      } else {
        openNotification("error", res.message ?? "Failed to load shifts");
      }
    } catch (error) {
      openNotification("error", "An error occurred while loading shifts");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const handleOk = () => {
    onSubmit(selectedRowKeys, selectedRows);
  };

  const filteredShifts = allShifts.filter((shift) =>
    (shift.staff_name || "").toLowerCase().includes(searchText.toLowerCase()),
  );

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys: React.Key[], rows: ShiftDataType[]) => {
      setSelectedRowKeys(keys);
      setSelectedRows(rows);
    },
  };

  const columns: TableColumnsType<ShiftDataType> = [
    {
      title: "Date",
      dataIndex: "date_report",
      key: "date_report",
      align: "left",
      width: 140,
    },
    {
      title: "Staff Report",
      dataIndex: "staff_name",
      key: "staff_name",
      align: "left",
      width: 140,
    },
    {
      title: "Bank",
      dataIndex: "addbank",
      key: "addbank",
      align: "right",
      render: (v) => <span>{numberFormat(v)}</span>,
    },
    {
      title: "Profit machine",
      dataIndex: "profit",
      key: "profit",
      align: "right",
      render: (v) => <span>{numberFormat(v)}</span>,
    },
    {
      title: "Money of system",
      dataIndex: "money_of_system",
      key: "money_of_system",
      align: "right",
      render: (v) => <span>{numberFormat(v)}</span>,
    },
    {
      title: "Cash on hand",
      dataIndex: "money_in_proof",
      key: "money_in_proof",
      align: "right",
      render: (v) => <span>{numberFormat(v)}</span>,
    },
    {
      title: "Zelle",
      dataIndex: "zelle",
      key: "zelle",
      align: "right",
      render: (v) => <span>{numberFormat(v)}</span>,
    },
    {
      title: "Baccarat",
      dataIndex: "baccarat",
      key: "baccarat",
      align: "right",
      render: (v) => <span>{numberFormat(v)}</span>,
    },
    {
      title: "Loss",
      key: "loss",
      align: "right",
      render: (_, record) => (
        <span>
          {numberFormat(record.loss + record.zelle - record?.baccarat)}
        </span>
      ),
    },
    {
      title: "Final",
      key: "final",
      align: "right",
      render: (_, record) => {
        const finalNumber =
          record.money_of_system - record.addbank + (record.baccarat || 0);
        return (
          <span style={{ color: finalNumber < 0 ? "#FD8181" : "#55c790" }}>
            {numberFormat(finalNumber)}
          </span>
        );
      },
    },
    {
      title: "Bonus",
      dataIndex: "bonus_total",
      key: "bonus_total",
      align: "right",
      render: (v) => <span>{numberFormat(v)}</span>,
    },
    {
      title: "Labor",
      dataIndex: "labor",
      key: "labor",
      align: "right",
      render: (v) => <span>{numberFormat(v)}</span>,
    },
    {
      title: "Match",
      dataIndex: "match_total",
      key: "match_total",
      align: "right",
      render: (v) => <span>{numberFormat(v)}</span>,
    },
    {
      title: "Extra",
      dataIndex: "extra",
      key: "extra",
      align: "right",
      render: (v) => <span>{numberFormat(v)}</span>,
    },
    {
      title: "Payout of system",
      dataIndex: "payout_total",
      key: "payout_total",
      align: "right",
      render: (v) => <span>{numberFormat(v)}</span>,
    },
  ];

  return (
    <>
      {contextHolder}
      <Modal
        centered
        open={isShow}
        footer={null}
        onCancel={onClose}
        className="shift-filter-modal"
        title="Filter by shifts"
        width={1300}
      >
        <Flex vertical gap={16}>
          <div className="modal-filter-bar">
            <Input
              placeholder="Search by staff name"
              prefix={<SearchZoomIn1 size={20} variant="Bulk" />}
              className="rs-input search-input"
              allowClear
              value={searchText}
              onChange={handleSearch}
            />
          </div>

          <div className="modal-table-wrapper">
            <Table
              rowSelection={rowSelection}
              columns={columns}
              dataSource={filteredShifts}
              loading={loading || loading}
              pagination={{
                defaultPageSize: 1000,
                showSizeChanger: true,
                pageSizeOptions: ["50", "100", "200", "500", "1000"],
                position: ["bottomCenter"],
              }}
              scroll={{ x: true, y: 450 }}
              size="middle"
            />
          </div>

          <div className="modal-footer-btns">
            <Button className="rs-button" onClick={onClose}>
              Cancel
            </Button>
            <Button
              className="rs-button danger"
              style={{ backgroundColor: "var(--primary-color, #1890ff)" }}
              onClick={handleOk}
            >
              Submit
            </Button>
          </div>
        </Flex>
      </Modal>
    </>
  );
};

export default ShiftFilterModal;

